/**
 * Finance Service - Serviço para gerenciamento de dados financeiros
 *
 * Fornece métodos para buscar dados consolidados do dashboard financeiro,
 * incluindo plano, uso, cartões, faturas e assinatura.
 */

import { api } from './api';

// ============= TIPOS =============

export interface PlanUsage {
  used: number;
  total: number;
  remaining: number;
  percentage: number;
  renew_days: number;
  period_start: string | null;
  period_end: string | null;
  extra_plays: number;
  auto_recharge_active: boolean;
}

export interface PlanData {
  name: string;
  type: string;
  price: string;
  original_price_cents: number;
  next_billing: string | null;
  currency: string;
  usage: PlanUsage;
}

export interface PaymentMethod {
  id: string;
  id_stripe: string;
  type: string;
  last4: string;
  holder: string;
  expiry_month: number;
  expiry_year: number;
  is_default: boolean;
  brand: string;
}

export interface Invoice {
  id: string;
  date: string;
  amount: string;
  amount_cents: number;
  status: string;
  is_subscription?: boolean;
  pdf_url: string;
  hosted_url: string;
  due_date?: string | null;
  description: string;
}

export interface DiscountData {
  coupon_code: string;
  coupon_name: string;
  type: 'percent' | 'fixed';
  value: number;
  discount_amount: number;
  discount_formatted: string;
}

export interface SubscriptionData {
  id: string;
  id_stripe: string;
  status: string;
  has_active_subscription: boolean;
  original_amount: number;
  original_amount_formatted: string;
  final_amount: number;
  final_amount_formatted: string;
  currency: string;
  current_period_start: string;
  current_period_end: string;
  discount: DiscountData | null;
}

export interface FinanceDashboardData {
  plan: PlanData;
  usage: PlanUsage;
  payment_methods: PaymentMethod[];
  invoices: Invoice[];
  subscription: SubscriptionData;
}

export interface FinanceApiResponse {
  success: boolean;
  message: string;
  data: FinanceDashboardData;
}

// ============= SERVICE =============

export const financeService = {
  /**
   * GET /finance/dashboard/:id_Stripe_Client
   * Busca todos os dados consolidados do dashboard financeiro
   */
  getDashboard: async (idStripeClient: string): Promise<FinanceDashboardData> => {
    const response = await api.get<FinanceApiResponse>(
      `/finance/dashboard/${idStripeClient}`
    );
    return response.data;
  },
};

export default financeService;
